// "Running low" — ONE warehouse-aware rule, shared by Inventory and Home.
//
// WHY THIS EXISTS. A reorder rule can be bound to a single warehouse
// (`reorder_rules.warehouse_id`); Demo Decor's seventeen rules are all bound to the
// Manchester site. Every screen that counted low stock ignored that column and matched a
// rule to stock by product/SKU alone, so the three surfaces disagreed on the same
// data and none of them was right:
//
//   Home              applied each Manchester rule to EVERY site's stock separately →
//                     "16 items running low", in red, mostly at sites the rule
//                     doesn't cover.
//   Reports attention same arithmetic → "16 items are low on stock".
//   Inventory         compared a single-site threshold against stock summed across
//                     all four sites → "None · all above reorder point".
//
// The truthful answer is 7: the rule is a threshold FOR ITS WAREHOUSE, so it must be
// read against that warehouse's stock. A rule with no warehouse_id is org-wide and
// reads against the total, which is how single-site orgs have always behaved.

// The rule that applies to an item, honouring warehouse binding. `warehouseId` is the
// site currently being viewed, or null for the whole group.
export function ruleFor(rules, item, warehouseId = null) {
  const matches = (r) =>
    (r.product_id && item.productId && r.product_id === item.productId) ||
    (r.sku && item.sku && r.sku === item.sku);
  const usable = (rules || []).filter((r) => r.is_active !== false && r.min_qty != null && matches(r));
  if (!usable.length) return null;
  if (warehouseId) {
    // Viewing one site: its own rule, else an org-wide rule. A rule for a DIFFERENT
    // site is not a threshold for this one and must never be borrowed.
    return usable.find((r) => r.warehouse_id === warehouseId) || usable.find((r) => !r.warehouse_id) || null;
  }
  // Group view: any rule the item has. Which stock it is read against is decided by
  // availableAgainst() below, per rule.
  return usable.find((r) => !r.warehouse_id) || usable[0] || null;
}

// The stock a rule should be read against: its own warehouse's when it is bound to
// one and we can see the per-site split, otherwise the item's total.
export function availableAgainst(rule, item) {
  if (rule && rule.warehouse_id && Array.isArray(item.sites) && item.sites.length) {
    const site = item.sites.find((s) => s.warehouseId === rule.warehouse_id);
    // A bound rule whose site holds no balance row at all means zero there — which is
    // as low as stock gets, and is exactly the case worth surfacing.
    return site ? Number(site.availableNum || 0) : 0;
  }
  return Number(item.availableNum ?? item.stockNum ?? 0);
}

// Is this item at or below its reorder point? Returns false when no rule applies —
// no threshold means no judgement, never a default of "low".
export function isLowStock(rules, item, warehouseId = null) {
  const rule = ruleFor(rules, item, warehouseId);
  if (!rule) return false;
  return availableAgainst(rule, item) <= Number(rule.min_qty);
}

// How many of these items are running low. Used by Home's tile and the Inventory hub
// card so the launcher and the page it opens cannot print different numbers.
export function countLowStock(rules, items, warehouseId = null) {
  return (items || []).filter((i) => isLowStock(rules, i, warehouseId)).length;
}
